import type { EnvironmentId } from "@cmmd-forge/contracts";

import { setSavedEnvironmentRegistry } from "~/clientPersistenceApi";

import type { EnvironmentConnection } from "./connection";
import type { SavedEnvironmentRecord } from "./catalog";

export type SavedEnvironmentAcquisitionRegistryExpectation =
  | { readonly kind: "existing-record"; readonly record: SavedEnvironmentRecord }
  | { readonly kind: "initial-add" };

type PendingAcquisition = {
  readonly generation: number;
  readonly record: SavedEnvironmentRecord;
  readonly promise: Promise<EnvironmentConnection>;
};

export class SavedEnvironmentConnectionRegistry {
  private readonly generations = new Map<EnvironmentId, number>();
  private readonly acquisitions = new Map<EnvironmentId, PendingAcquisition>();

  constructor(
    private readonly readRecord: (environmentId: EnvironmentId) => SavedEnvironmentRecord | null,
    private readonly hasSameTarget: (
      left: SavedEnvironmentRecord,
      right: SavedEnvironmentRecord,
    ) => boolean,
  ) {}

  invalidate(environmentId: EnvironmentId): void {
    this.generations.set(environmentId, this.readGeneration(environmentId) + 1);
  }

  generationFor(environmentId: EnvironmentId): number {
    return this.readGeneration(environmentId);
  }

  isCurrent(input: {
    readonly record: SavedEnvironmentRecord;
    readonly generation: number;
    readonly registryExpectation: SavedEnvironmentAcquisitionRegistryExpectation;
  }): boolean {
    if (this.readGeneration(input.record.environmentId) !== input.generation) return false;
    const currentRecord = this.readRecord(input.record.environmentId);
    if (input.registryExpectation.kind === "initial-add") {
      return currentRecord === null || this.hasSameTarget(currentRecord, input.record);
    }
    return (
      currentRecord !== null && this.hasSameTarget(currentRecord, input.registryExpectation.record)
    );
  }

  ensure(
    record: SavedEnvironmentRecord,
    create: (record: SavedEnvironmentRecord, generation: number) => Promise<EnvironmentConnection>,
  ): Promise<EnvironmentConnection> {
    const generation = this.readGeneration(record.environmentId);
    const pending = this.acquisitions.get(record.environmentId);
    if (
      pending &&
      pending.generation === generation &&
      this.hasSameTarget(pending.record, record)
    ) {
      return pending.promise;
    }
    const promise = create(record, generation).finally(() => {
      const current = this.acquisitions.get(record.environmentId);
      if (current?.promise === promise) this.acquisitions.delete(record.environmentId);
    });
    this.acquisitions.set(record.environmentId, { generation, record, promise });
    return promise;
  }

  async sync(input: {
    readonly records: ReadonlyArray<SavedEnvironmentRecord>;
    readonly connectedSavedEnvironmentIds: ReadonlyArray<EnvironmentId>;
    readonly disconnect: (environmentId: EnvironmentId) => Promise<void>;
  }): Promise<void> {
    await setSavedEnvironmentRegistry(input.records);
    const recordsById = new Map(input.records.map((record) => [record.environmentId, record]));
    for (const [environmentId, acquisition] of this.acquisitions) {
      const currentRecord = recordsById.get(environmentId);
      if (!currentRecord || !this.hasSameTarget(currentRecord, acquisition.record)) {
        this.invalidate(environmentId);
      }
    }
    const expectedIds = new Set(recordsById.keys());
    await Promise.all(
      input.connectedSavedEnvironmentIds
        .filter((environmentId) => !expectedIds.has(environmentId))
        .map(async (environmentId) => {
          this.invalidate(environmentId);
          await input.disconnect(environmentId);
        }),
    );
  }

  clear(): void {
    this.generations.clear();
    this.acquisitions.clear();
  }

  private readGeneration(environmentId: EnvironmentId): number {
    return this.generations.get(environmentId) ?? 0;
  }
}
